import type { AppState } from ".."
import type {
  LocationLookupResponse,
  PaginatedListOfTopographyDto,
  PagingAction,
  PagingOptions,
  TopographyDto
} from "../../models"
import {
  deleteTopography,
  getGoogleApiCallCount,
  getTopographicList,
  getTopographyById,
  postTopography,
  putTopography,
  searchLocation,
  verifyTopography
} from "../../services"
import type { AppThunkAction, AppThunkDispatch } from "../app.types"
import type {
  Topography,
  TopographyAction,
  TopographyFilterAction,
  TopographyListAction,
  TopographyLookupAction,
  TopographyLookupCountAction
} from "./topography.types"

export const TOPOGRAPHY_FETCH_LIST: string = "TOPOGRPAHY_FETCH_LIST"
export const TOPOGRAPHY_LOAD_LIST: string = "TOPOGRAPHY_LOAD_LIST"
export const TOPOGRAPHY_APPEND_LIST: string = "TOPOGRAPHY_APPEND_LIST"
export const TOPOGRAPHY_CREATE: string = "TOPOGRAPHY_CREATE"
export const TOPOGRAPHY_LOAD: string = "TOPOGRAPHY_LOAD"
export const TOPOGRAPHY_UPDATE: string = "TOPOGRAPHY_UPDATE"
export const TOPOGRAPHY_DELETE: string = "TOPOGRAPHY_DELETE"
export const TOPOGRAPHY_CHANGE_PAGE: string = "TOPOGRAPHY_CHANGE_PAGE"
export const TOPOGRAPHY_LOAD_PAGING_OPTIONS: string = "TOPOGRAPHY_LOAD_PAGING_OPTIONS"
export const TOPOGRAPHY_SEARCH_LOCATION: string = "TOPOGRAPHY_SEARCH_LOCATION"
export const TOPOGRAPHY_LOAD_SEARCH_RESULTS: string = "TOPOGRAPHY_LOAD_SEARCH_RESULTS"
export const TOPOGRAPHY_API_CALL_COUNT: string = "TOPOGRAPHY_API_CALL_COUNT"
export const TOPOGRAPHY_LOAD_API_CALL_COUNT: string = "TOPOGRAPHY_LOAD_API_CALL_COUNT"
export const TOPOGRAPHY_CLEAR_SEARCH_RESULTS: string = "TOPOGRAPHY_CLEAR_SEARCH_RESULTS"
export const TOPOGRAPHY_SET_COORDS_FILTER: string = "TOPOGRAPHY_SET_COORDS_FILTER"
export const TOPOGRAPHY_REMOVE_FROM_LIST: string = "TOPOGRAPHY_REMOVE_FROM_LIST"

export const TOPOGRAPHY_VERIFY: string = "TOPOGRAPHY_VERIFY"

export function topographyFetchList(
  pageIndex = 0,
  pageSize = 100,
  append = false
): AppThunkAction<void, TopographyListAction> {
  return async (dispatch: AppThunkDispatch<TopographyListAction | PagingAction>, getState: () => AppState) => {
    dispatch({ type: TOPOGRAPHY_FETCH_LIST })

    const { filter } = getState().contentManager
    const { coordsFilter } = getState().topography
    const result: PaginatedListOfTopographyDto = await getTopographicList(pageIndex, pageSize, filter, coordsFilter)

    const pagingOptions: PagingOptions = {
      currentPage: result.pageIndex,
      totalPages: result.totalPages,
      pageSize
    }

    if (append) {
      dispatch(topographyAppendList(result.items!))
    } else {
      dispatch(topographyLoadList(result.items!))
    }

    dispatch(topographyLoadPagingOptions(pagingOptions))
  }
}

export function topographyLoadList(list: TopographyDto[]): TopographyListAction {
  return {
    type: TOPOGRAPHY_LOAD_LIST,
    payload: list.map((topo) => topoDtoToTopography(topo))
  }
}

export function topographyAppendList(list: TopographyDto[]): TopographyListAction {
  return {
    type: TOPOGRAPHY_APPEND_LIST,
    payload: list.map((topo) => topoDtoToTopography(topo))
  }
}

export function topographyChangePage(page: number): AppThunkAction<void, PagingAction> {
  return (dispatch: AppThunkDispatch<PagingAction>, getState: () => AppState) => {
    dispatch({ type: TOPOGRAPHY_CHANGE_PAGE })
    const { pageSize } = getState().topography.pagingOptions
    dispatch(topographyFetchList(page, pageSize, true))
  }
}

export function topographyLoadPagingOptions(pagingOptions: PagingOptions): PagingAction {
  return { type: TOPOGRAPHY_LOAD_PAGING_OPTIONS, payload: pagingOptions }
}

export function topographyCreate(
  name: string,
  lat: string,
  lng: string,
  page: number,
  letter?: string,
  suffix?: string,
  metadataId?: string
): AppThunkAction<void, TopographyAction> {
  return async (dispatch: AppThunkDispatch<TopographyAction>) => {
    dispatch({ type: TOPOGRAPHY_CREATE })

    const topographyId: string = await postTopography(name, lat, lng, page, letter, suffix, metadataId)
    const topography: TopographyDto = await getTopographyById(topographyId)

    dispatch(topographyFetchList())

    dispatch(topographyLoad(topography))
  }
}

export function topographyLoad(topography?: TopographyDto | Topography): TopographyAction {
  return {
    type: TOPOGRAPHY_LOAD,
    payload: topography ? topoDtoToTopography(topography) : undefined
  }
}

export function topographyVerify(id: string): AppThunkAction<void, TopographyAction> {
  return async (dispatch: AppThunkDispatch<TopographyAction>) => {
    dispatch({ type: TOPOGRAPHY_VERIFY })
    await verifyTopography(id)
    const updatedTopography: TopographyDto = await getTopographyById(id)
    dispatch(topographyLoad(updatedTopography))
  }
}

export function topographyUpdate(topography: Topography): AppThunkAction<void, TopographyAction> {
  return async (dispatch: AppThunkDispatch<TopographyAction>, getState: () => AppState) => {
    dispatch({ type: TOPOGRAPHY_UPDATE })
    const { coordsFilter } = getState().topography
    await putTopography(topography)
    const updatedTopography: TopographyDto = await getTopographyById(topography.id as string)
    dispatch(topographyLoad(updatedTopography))
    if (coordsFilter) dispatch(removeTopographyFromList(updatedTopography))
  }
}

export function topographyDelete(id: string): AppThunkAction<void, TopographyListAction> {
  return async (dispatch: AppThunkDispatch<TopographyListAction>) => {
    dispatch({ type: TOPOGRAPHY_DELETE })
    await deleteTopography(id)
    dispatch(topographyFetchList())
  }
}

export function topographySearchLocation(searchValue: string): AppThunkAction<void, TopographyLookupAction> {
  return async (dispatch: AppThunkDispatch<TopographyLookupAction>) => {
    dispatch({ type: TOPOGRAPHY_SEARCH_LOCATION })
    const results: LocationLookupResponse = await searchLocation(searchValue)
    dispatch(topographyLoadSearchResults(results))
  }
}

export function topographyLoadSearchResults(results: LocationLookupResponse): TopographyLookupAction {
  return { type: TOPOGRAPHY_LOAD_SEARCH_RESULTS, payload: results }
}

export function topographyFetchApiCallCount(): AppThunkAction<void, TopographyLookupCountAction> {
  return async (dispatch: AppThunkDispatch<TopographyLookupCountAction>) => {
    dispatch({ type: TOPOGRAPHY_API_CALL_COUNT })

    const count: number = await getGoogleApiCallCount()

    dispatch(topographyLoadApiCallCount(count))
  }
}

export function topographyLoadApiCallCount(count: number): TopographyLookupCountAction {
  return { type: TOPOGRAPHY_LOAD_API_CALL_COUNT, payload: count }
}

export function topographyClearSearchResults(): TopographyLookupAction {
  return { type: TOPOGRAPHY_CLEAR_SEARCH_RESULTS }
}

export function topographySetCoordsFilter(onlyWithoutCoords: boolean): TopographyFilterAction {
  return { type: TOPOGRAPHY_SET_COORDS_FILTER, payload: onlyWithoutCoords }
}

export function removeTopographyFromList(topography: Topography): TopographyAction {
  return {
    type: TOPOGRAPHY_REMOVE_FROM_LIST,
    payload: topography
  }
}

export function topoDtoToTopography(topoDto: TopographyDto | Topography): Topography {
  let metadataId: string | undefined
  if ("metadata" in topoDto) metadataId = topoDto.metadata?.id
  if ("metadataId" in topoDto) metadataId = topoDto.metadataId
  return {
    id: topoDto.id,
    name: topoDto.name,
    lat: topoDto.lat,
    lng: topoDto.lng,
    page: topoDto.page,
    suffix: topoDto.suffix,
    letter: topoDto.letter,
    metadataId,
    verified: topoDto.verified
  }
}
